import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, TextField,
  MenuItem, Alert, CircularProgress, IconButton, Tooltip,
  Card, CardContent
} from '@mui/material';
import {
  HelpOutline as QueryIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { teacherAPI } from '../../services/api';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const STATUS_COLORS = { open: 'warning', in_progress: 'info', completed: 'success', void: 'default' };
const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', completed: 'Completed', void: 'Void' };

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function ParentQueries() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [queries, setQueries]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [completeDialog, setCompleteDialog] = useState(null); // { query }
  const [resNote, setResNote]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teacherAPI.queries.queue(statusFilter ? { status: statusFilter } : {});
      setQueries(res.data.queries || []);
    } catch {
      setError('Failed to load queries');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleStart = async (id) => {
    setError('');
    try {
      await teacherAPI.queries.start(id);
      setSuccess('Query marked in-progress');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update query');
    }
  };

  const handleComplete = async () => {
    setError('');
    try {
      await teacherAPI.queries.complete(completeDialog.id, resNote);
      setCompleteDialog(null); setResNote('');
      setSuccess('Query marked completed. Parent notified.');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete query');
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <QueryIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>Parent Queries</Typography>
              <Typography variant="body2" color="text.secondary">Resolve parent support requests</Typography>
            </Box>
          </Box>
          <Box sx={{ ml: { sm: 'auto' } }} display="flex" gap={1}>
            <TextField
              select size="small" label="Status" value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </TextField>
            <Tooltip title="Refresh">
              <IconButton onClick={load}><RefreshIcon /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
        ) : queries.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <QueryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No queries found.</Typography>
          </Paper>
        ) : (
          <ResponsiveDataView
            rows={queries}
            keyExtractor={(q) => q.id}
            renderCard={(q) => (
              <Card variant="outlined" sx={{ mb: 1.5 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Typography variant="body2" fontWeight={600}>{q.subject}</Typography>
                    <Chip label={STATUS_LABELS[q.status]} color={STATUS_COLORS[q.status]} size="small" />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {q.body}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {q.student_name} · from {q.raised_by_name} · {fmtDate(q.created_at)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Assigned to: {q.assigned_to_name || 'Pool'}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {q.status === 'open' && (
                      <Button size="small" variant="outlined" startIcon={<StartIcon />} onClick={() => handleStart(q.id)}>
                        Start
                      </Button>
                    )}
                    {(q.status === 'open' || q.status === 'in_progress') && (
                      <Button
                        size="small" variant="contained" color="success" startIcon={<CompleteIcon />}
                        onClick={() => { setCompleteDialog(q); setResNote(''); }}
                      >
                        Complete
                      </Button>
                    )}
                    {q.status === 'completed' && q.resolution_note && (
                      <Typography variant="caption" color="text.secondary">
                        {q.resolution_note.slice(0, 60)}{q.resolution_note.length > 60 ? '…' : ''}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
            table={
              <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Subject</strong></TableCell>
                      <TableCell><strong>Student</strong></TableCell>
                      <TableCell><strong>From</strong></TableCell>
                      <TableCell><strong>Raised</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Assigned To</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queries.map(q => (
                      <TableRow key={q.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{q.subject}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {q.body}
                          </Typography>
                        </TableCell>
                        <TableCell>{q.student_name}</TableCell>
                        <TableCell>{q.raised_by_name}</TableCell>
                        <TableCell>{fmtDate(q.created_at)}</TableCell>
                        <TableCell>
                          <Chip label={STATUS_LABELS[q.status]} color={STATUS_COLORS[q.status]} size="small" />
                        </TableCell>
                        <TableCell>{q.assigned_to_name || <em style={{ color: '#aaa' }}>Pool</em>}</TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={0.5} justifyContent="center">
                            {q.status === 'open' && (
                              <Tooltip title="Take this query">
                                <Button size="small" variant="outlined" startIcon={<StartIcon />} onClick={() => handleStart(q.id)}>
                                  Start
                                </Button>
                              </Tooltip>
                            )}
                            {(q.status === 'open' || q.status === 'in_progress') && (
                              <Tooltip title="Mark completed">
                                <Button
                                  size="small" variant="contained" color="success" startIcon={<CompleteIcon />}
                                  onClick={() => { setCompleteDialog(q); setResNote(''); }}
                                >
                                  Complete
                                </Button>
                              </Tooltip>
                            )}
                            {q.status === 'completed' && q.resolution_note && (
                              <Typography variant="caption" color="text.secondary">
                                {q.resolution_note.slice(0, 40)}{q.resolution_note.length > 40 ? '…' : ''}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            }
          />
        )}
      </Container>

      {/* Complete dialog */}
      <MobileFormDialog
        open={!!completeDialog}
        onClose={() => setCompleteDialog(null)}
        title="Complete Query"
        actions={
          <>
            <Button onClick={() => setCompleteDialog(null)}>Cancel</Button>
            <Button variant="contained" color="success" startIcon={<CompleteIcon />} onClick={handleComplete}>
              Mark Completed
            </Button>
          </>
        }
      >
        <Typography mb={2}>
          Resolving: <strong>{completeDialog?.subject}</strong> for {completeDialog?.student_name}
        </Typography>
        <TextField
          fullWidth multiline minRows={3} label="Resolution note (optional)"
          placeholder="Describe how the query was resolved…"
          value={resNote} onChange={e => setResNote(e.target.value)}
        />
        <Typography variant="caption" color="text.secondary" mt={1} display="block">
          The parent will receive an in-app notification when you complete.
        </Typography>
      </MobileFormDialog>
    </>
  );
}
